import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MilestonePhaseDto } from 'src/api/FiretrolApiClient';
import { milestonePhaseApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { toCancelToken } from 'src/api/queries/queryClient';

/** @registry Milestone phases defined on a job. */
export function useJobMilestonePhases(jobId: number | null | undefined) {
  return useQuery({
    queryKey: queryKeys.milestonePhases.forJob(jobId ?? -1),
    queryFn: ({ signal }) =>
      milestonePhaseApiClient.listJobPhases(jobId!, toCancelToken(signal)),
    enabled: jobId != null && jobId > 0,
    staleTime: 30_000,
    meta: { errorContext: 'job milestone phases' },
  });
}

/**
 * Template phases come back as one flat list for every template; `templateId`
 * only narrows what the caller sees.
 */
export function useTemplateMilestonePhases(
  templateId: string | null | undefined,
  enabled = true,
) {
  const select = useCallback(
    (phases: MilestonePhaseDto[]) =>
      templateId ? phases.filter((p) => p.templateId === templateId) : phases,
    [templateId],
  );
  return useQuery({
    queryKey: queryKeys.milestonePhases.forTemplate(templateId ?? 'all'),
    queryFn: ({ signal }) =>
      milestonePhaseApiClient.listTemplatePhases(toCancelToken(signal)),
    enabled,
    staleTime: 30_000,
    select,
    meta: { errorContext: 'template milestone phases' },
  });
}
